/*
 * Photo Cutouts: the ES CRM enquiry form (sections/pco-crm-contact-form.liquid).
 *
 * Ported from the old theme's es-crm-form.js. It posts the same JSON to the
 * same app proxy, /apps/es-crm/form, with the same keys: email, first_name,
 * last_name, phone, message, marketing_opt_in, fields.form_type,
 * fields.privacy_consent and any [data-es-crm-field] inputs, plus the
 * attribution keys added by pco-crm-attribution.js. The CRM's route
 * (ES CRM, src/app/api/shopify/proxy/form/route.ts) validates that shape;
 * change it there first.
 *
 * Differences from the old script: the email is checked for shape as well as
 * presence (the CRM rejects a malformed one with an error nobody could read),
 * the field at fault is marked, tied to the error text (aria-describedby) and
 * focused, any failed reply shows the generic message (never the CRM's own
 * error text, which is written for us rather than the buyer), and on success
 * the form gives way to the thank-you message.
 */
(function () {
  var ENDPOINT = '/apps/es-crm/form';
  var EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function messageFor(root, key, fallback) {
    var attr = root.getAttribute('data-es-crm-msg-' + key);
    return attr && attr.trim() ? attr : fallback;
  }

  function markValid(el) {
    el.removeAttribute('aria-invalid');
    var errorId = el.getAttribute('data-es-crm-described');
    if (!errorId) return;
    var ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (id) {
      return id && id !== errorId;
    });
    if (ids.length) el.setAttribute('aria-describedby', ids.join(' '));
    else el.removeAttribute('aria-describedby');
    el.removeAttribute('data-es-crm-described');
  }

  function clearInvalid(form) {
    form.querySelectorAll('[aria-invalid="true"]').forEach(markValid);
  }

  function showError(root, errorEl, text, field) {
    if (errorEl) {
      errorEl.textContent = text;
      errorEl.hidden = false;
    }
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      if (errorEl && errorEl.id) {
        var ids = (field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        if (ids.indexOf(errorEl.id) === -1) ids.push(errorEl.id);
        field.setAttribute('aria-describedby', ids.join(' '));
        field.setAttribute('data-es-crm-described', errorEl.id);
      }
      field.focus();
    } else if (errorEl) {
      errorEl.focus();
    }
  }

  function value(formData, name) {
    return String(formData.get(name) || '').trim();
  }

  var ADS_SEND_TO_RE = /^AW-\d{4,20}\/[A-Za-z0-9_-]{4,64}$/;

  // Reads Shopify's Customer Privacy API. Returns true/false, or null when it
  // is not loaded or cannot be read.
  function readMarketingAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === "function") {
        return cp.marketingAllowed() === true;
      }
    } catch (e) {}
    return null;
  }

  // Calls back with true only when the visitor has allowed marketing.
  // Anything undeterminable counts as "no".
  function whenMarketingAllowed(callback) {
    var now = readMarketingAllowed();
    if (now !== null) return callback(now);
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
        window.Shopify.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          function (error) {
            callback(error ? false : readMarketingAllowed() === true);
          },
        );
        return;
      }
    } catch (e) {}
    callback(false);
  }

  var adsConfigured = {};

  function randomTransactionId() {
    return "es-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  // One Google Ads lead conversion per successful submit. No personal data.
  function sendAdsConversion(sendTo, transactionId) {
    var awId = sendTo.split("/")[0];
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }
    if (!document.querySelector('script[data-es-crm-gtag="' + awId + '"]')) {
      var hasGtagJs = Array.prototype.some.call(
        document.querySelectorAll("script[src]"),
        function (s) {
          return s.src.indexOf("googletagmanager.com/gtag/js") !== -1;
        },
      );
      if (!hasGtagJs) {
        var tag = document.createElement("script");
        tag.async = true;
        tag.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(awId);
        tag.setAttribute("data-es-crm-gtag", awId);
        document.head.appendChild(tag);
        window.gtag("js", new Date());
      }
    }
    if (!adsConfigured[awId]) {
      adsConfigured[awId] = true;
      window.gtag("config", awId);
    }
    window.gtag("event", "conversion", {
      send_to: sendTo,
      transaction_id: transactionId,
    });
  }

  // Lead-sent signal. Only called after the CRM accepted the submission.
  function trackLead(root, formType, body) {
    try {
      document.dispatchEvent(
        new CustomEvent("es-crm:lead", { detail: { formType: formType } }),
      );
    } catch (e) {}
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "generate_lead", form_type: formType });
    } catch (e) {}
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish("es_crm_lead", { form_type: formType });
      }
    } catch (e) {}
    try {
      var sendTo = (root.getAttribute("data-es-crm-ads-send-to") || "").trim();
      if (!ADS_SEND_TO_RE.test(sendTo)) return;
      // Spam / suppressed submissions are accepted quietly: not a real lead.
      if (body && body.suppressed) return;
      if (body && "submission_id" in body && !body.submission_id) return;
      var transactionId =
        body && body.submission_id ? String(body.submission_id) : randomTransactionId();
      whenMarketingAllowed(function (allowed) {
        if (!allowed) return;
        try {
          sendAdsConversion(sendTo, transactionId);
        } catch (e) {}
      });
    } catch (e) {}
  }
  function initForm(root) {
    if (!root || root.dataset.esCrmBound === '1') return;
    root.dataset.esCrmBound = '1';

    var form = root.querySelector('form');
    var successEl = root.querySelector('[data-es-crm-success]');
    var errorEl = root.querySelector('[data-es-crm-error]');
    if (!form) return;

    form.addEventListener('input', function (event) {
      if (event.target && event.target.getAttribute && event.target.getAttribute('aria-invalid') === 'true') {
        markValid(event.target);
      }
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (errorEl) errorEl.hidden = true;
      if (successEl) successEl.hidden = true;
      clearInvalid(form);

      var formData = new FormData(form);
      var formType = root.getAttribute('data-es-crm-form-type') || 'general';
      var email = value(formData, 'email');
      var phone = value(formData, 'phone');
      var privacy = formData.get('privacy_consent') === 'on';
      var emailInput = form.querySelector('input[name="email"]');
      var phoneInput = form.querySelector('input[name="phone"]');
      var privacyInput = form.querySelector('input[name="privacy_consent"]');

      if (!email || !EMAIL_SHAPE.test(email)) {
        showError(root, errorEl, messageFor(root, 'email', 'Please enter a valid email.'), emailInput);
        return;
      }

      if (phoneInput && phoneInput.required && !phone) {
        showError(root, errorEl, messageFor(root, 'phone', 'Please enter a phone number.'), phoneInput);
        return;
      }

      if (!privacy) {
        showError(root, errorEl, messageFor(root, 'privacy', 'Please accept the privacy consent.'), privacyInput);
        return;
      }

      var payload = {
        email: email,
        first_name: value(formData, 'first_name') || undefined,
        last_name: value(formData, 'last_name') || undefined,
        phone: phone || undefined,
        message: value(formData, 'message') || undefined,
        marketing_opt_in: formData.get('marketing_opt_in') === 'on',
        fields: {
          form_type: formType,
          privacy_consent: true,
        },
      };

      root.querySelectorAll('[data-es-crm-field]').forEach(function (input) {
        var key = input.getAttribute('data-es-crm-field');
        if (!key) return;
        var v = 'value' in input ? String(input.value).trim() : '';
        if (v) payload.fields[key] = v;
      });

      if (window.esCrmAttribution && window.esCrmAttribution.applyToFormPayload) {
        payload = window.esCrmAttribution.applyToFormPayload(payload);
      }

      var generic = messageFor(root, 'generic', 'Something went wrong. Please try again.');
      var submitBtn = form.querySelector('[type=submit]');
      if (submitBtn) submitBtn.disabled = true;
      root.setAttribute('aria-busy', 'true');

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          return res
            .json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              if (!res.ok || (body && body.ok === false)) throw new Error('failed');
              return body;
            });
        })
        .then(function (body) {
          form.reset();
          form.hidden = true;
          if (successEl) {
            successEl.hidden = false;
            successEl.focus();
          }
          trackLead(root, formType, body);
        })
        .catch(function () {
          showError(root, errorEl, generic, null);
        })
        .finally(function () {
          if (submitBtn) submitBtn.disabled = false;
          root.removeAttribute('aria-busy');
        });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-es-crm-form]').forEach(initForm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
